import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, apiErr } from "../lib/api";
import { money } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { useRoles } from "../lib/useRoles";
import { useClientPerms } from "../lib/useClientPerms";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, XCircle, ShieldCheck, Undo2, ChevronRight } from "lucide-react";

const STATUS_STEPS = ["CALCULATED", "REVIEWED", "APPROVED", "POSTED"];
const CHECK_ICON = { PASS: [CheckCircle2, "text-emerald-600"], WARNING: [AlertTriangle, "text-amber-600"],
  OVERRIDDEN: [ShieldCheck, "text-sky-600"], FAIL: [XCircle, "text-red-600"], NOT_APPLICABLE: [ChevronRight, "text-muted-foreground"] };

export default function RunWorkflowPanel({ run, onChanged }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const base = useLocation().pathname.startsWith("/client") ? "/client" : "";
  const runId = run.run_id;
  const roleBase = useRoles().base;
  const role = user?.role === "super_admin" ? "admin" : roleBase("client", user?.client_role || "viewer");
  const { canEdit } = useClientPerms();
  const wfEdit = canEdit("run_payroll");
  const isAdmin = wfEdit && (role === "admin" || user?.role === "super_admin");
  const isPreparer = wfEdit && (role === "preparer" || isAdmin);
  const [recon, setRecon] = useState(null);
  const [status, setStatus] = useState(run.workflow_status || (run.status === "completed" ? "POSTED_LEGACY" : "DRAFT"));
  const [version, setVersion] = useState(run.version || 1);
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [audit, setAudit] = useState([]);
  const [invalidation, setInvalidation] = useState(run.last_invalidation || null);

  const load = useCallback(() => {
    api.get(`/payroll/runs/${runId}/reconciliation`).then((r) => {
      setRecon(r.data.reconciliation); setStatus(r.data.workflow_status); setVersion((v) => r.data.version || v);
      setInvalidation(r.data.last_invalidation || null);
    }).catch(() => {});
    api.get(`/payroll/runs/${runId}/workflow-audit`).then((r) => setAudit(r.data || [])).catch(() => {});
  }, [runId]);
  useEffect(() => { load(); }, [load]);

  const act = async (action, body = {}) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/payroll/runs/${runId}/${action}`, { version, ...body });
      toast.success(`Payroll ${action === "post" ? "posted" : action + "d"}`);
      load(); onChanged && onChanged();
      return data;
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "reconciliation_failed") toast.error("🔴 Payroll cannot be posted — fix the blocking checks below.");
      else toast.error(apiErr(e));
      load();
    } finally { setBusy(false); }
  };

  const doOverride = async (code) => {
    const reason = window.prompt("Override reason for this warning:");
    if (!reason) return;
    await act("override", { check_code: code, reason });
  };
  const doReverse = async () => {
    const reason = window.prompt("Reason for reversing this posted payroll:");
    if (!reason) return;
    setBusy(true);
    try { await api.post(`/payroll/runs/${runId}/reverse`, { note: reason }); toast.success("Payroll reversed"); load(); onChanged && onChanged(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const doRecalc = async () => {
    if (!window.confirm("Recalculate payroll?\n\nPayroll inputs changed after the previous review/approval. Recalculating will regenerate the unposted payroll using the latest employee, YTD, tax and company inputs.\n\nThe payroll will require review and approval again.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/payroll/runs/${runId}/recalculate`, { version });
      const c = data.comparison || {};
      toast.success(c.no_change
        ? "Recalculation complete — no payroll amounts changed. Review & approval still required."
        : `Payroll recalculated — ${c.employees_changed} employee(s) changed. Review & approval required.`);
      navigate(`${base}/history/${data.new_run_id}`);
      onChanged && onChanged();
    } catch (e) { toast.error(apiErr(e)); load(); } finally { setBusy(false); }
  };

  const s = recon?.summary;
  const posted = status === "POSTED" || status === "POSTED_LEGACY";
  const legacy = status === "POSTED_LEGACY";
  const stepIdx = STATUS_STEPS.indexOf(status === "POSTED_LEGACY" ? "POSTED" : status);

  const bannerCfg = !s ? null : s.overall === "FAIL"
    ? { c: "border-red-500 bg-red-50 text-red-800", Icon: XCircle, t: "🔴 Payroll cannot be posted" }
    : s.overall === "WARNING" ? { c: "border-amber-500 bg-amber-50 text-amber-800", Icon: AlertTriangle, t: "Reconciliation passed with warnings" }
    : { c: "border-emerald-500 bg-emerald-50 text-emerald-800", Icon: CheckCircle2, t: "All reconciliation checks passed" };

  return (
    <div className="border border-black mb-8" data-testid="run-workflow-panel" data-status={status}>
      <div className="bg-neutral-50 px-4 py-3 border-b border-border flex flex-wrap items-center gap-3">
        <span className="text-sm font-black tracking-tight">Payroll workflow</span>
        <span data-testid="workflow-status-badge" className={`text-xs px-2 py-1 font-medium ${posted ? "bg-emerald-100 text-emerald-700" : status === "REVERSED" ? "bg-neutral-200 text-neutral-600" : "bg-sky-100 text-sky-700"}`}>{status}{legacy ? " (legacy)" : ""}</span>
        {run.is_reversal && <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700">Reversal of {run.reverses_run_id}</span>}
        {run.reversed_by_run && <span className="text-xs px-2 py-1 bg-red-100 text-red-700">Reversed</span>}
        {/* stepper */}
        {!legacy && !run.is_reversal && (
          <div className="ml-auto flex items-center gap-1.5 text-[11px]">
            {STATUS_STEPS.map((st, i) => (
              <span key={st} className="flex items-center gap-1.5">
                <span className={`px-2 py-0.5 ${i <= stepIdx ? "bg-black text-white" : "bg-neutral-200 text-muted-foreground"}`}>{st}</span>
                {i < 3 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="p-4 space-y-4">
        {invalidation && !posted && (status === "CALCULATED") && (
          <div className="border-l-4 border-amber-500 bg-amber-50 px-4 py-2.5 text-sm text-amber-800" data-testid="invalidation-notice">
            <span className="font-semibold">{invalidation.invalidated_from === "APPROVED" ? "Approval invalidated" : "Review invalidated"}</span>
            {" — "}{invalidation.trigger}{invalidation.invalidated_by ? ` by ${invalidation.invalidated_by}` : ""}
            {invalidation.invalidated_at ? ` on ${String(invalidation.invalidated_at).slice(0, 16).replace("T", " ")}` : ""}.
            {" Reconciliation, review and approval are required again."}
            <div className="mt-2">
              {isPreparer
                ? <button data-testid="wf-recalculate" disabled={busy} onClick={doRecalc} className="inline-flex items-center gap-1.5 bg-black text-white px-3 py-1.5 text-xs font-medium hover:opacity-90 disabled:opacity-50">Reopen &amp; Recalculate</button>
                : <span className="text-xs text-amber-700" data-testid="wf-recalc-denied">Payroll inputs changed. A payroll processor must recalculate this run.</span>}
            </div>
          </div>
        )}
        {run.recalc_comparison && !run.recalc_comparison.no_change && (
          <div className="border border-border bg-neutral-50 px-4 py-2.5 text-xs" data-testid="recalc-comparison">
            <div className="font-semibold text-sm mb-1">Payroll recalculated</div>
            <div>Gross payroll: {money(run.recalc_comparison.gross.before)} → <b>{money(run.recalc_comparison.gross.after)}</b></div>
            <div>CRA liability: {money(run.recalc_comparison.cra_liability.before)} → <b>{money(run.recalc_comparison.cra_liability.after)}</b></div>
            <div>Net payroll: {money(run.recalc_comparison.net_pay.before)} → <b>{money(run.recalc_comparison.net_pay.after)}</b></div>
            <div className="text-muted-foreground mt-0.5">Employees changed: {run.recalc_comparison.employees_changed}</div>
          </div>
        )}
        {run.recalc_comparison && run.recalc_comparison.no_change && (
          <div className="border border-border bg-neutral-50 px-4 py-2 text-xs text-muted-foreground" data-testid="recalc-comparison">Recalculation complete — no payroll amounts changed. Review and approval are still required.</div>
        )}
        {legacy ? (
          <p className="text-sm text-muted-foreground" data-testid="workflow-legacy-note">Legacy finalized payroll — posted before the Phase 3 workflow. Historical values are preserved and authoritative.</p>
        ) : (
          <>
            {bannerCfg && (
              <div className={`border-l-4 ${bannerCfg.c} px-4 py-2.5 flex items-center gap-3`} data-testid="reconciliation-banner">
                <bannerCfg.Icon className="h-5 w-5" />
                <span className="text-sm font-semibold">{bannerCfg.t}</span>
                {s && <span className="text-xs">· {s.pass_count} passed · {s.warning_count} warnings · {s.blocking_count} blocking</span>}
              </div>
            )}

            {/* checks */}
            <div className="border border-border divide-y divide-border/60" data-testid="reconciliation-checks">
              {(recon?.checks || []).map((c) => {
                const [Icon, cls] = CHECK_ICON[c.status] || CHECK_ICON.NOT_APPLICABLE;
                const hasDetail = (c.employee_details || []).length > 0;
                return (
                  <div key={c.check_code} data-testid={`recon-check-${c.check_code}`}>
                    <div className="px-3 py-2 flex items-center gap-3 text-sm">
                      <Icon className={`h-4 w-4 ${cls} shrink-0`} />
                      <span className="font-medium">{c.check_name}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 ${c.status === "PASS" ? "bg-emerald-100 text-emerald-700" : c.status === "FAIL" ? "bg-red-100 text-red-700" : c.status === "WARNING" ? "bg-amber-100 text-amber-700" : c.status === "OVERRIDDEN" ? "bg-sky-100 text-sky-700" : "bg-neutral-100 text-muted-foreground"}`}>{c.status}</span>
                      <span className="text-xs text-muted-foreground flex-1 truncate">{c.message}</span>
                      {hasDetail && <button onClick={() => setExpanded((x) => ({ ...x, [c.check_code]: !x[c.check_code] }))} className="text-xs underline" data-testid={`recon-expand-${c.check_code}`}>{expanded[c.check_code] ? "hide" : "details"}</button>}
                      {c.status === "WARNING" && !c.blocking && isAdmin && !posted && <button data-testid={`recon-override-${c.check_code}`} onClick={() => doOverride(c.check_code)} className="text-xs border border-border px-2 py-0.5 hover:border-black transition-colors">Override</button>}
                    </div>
                    {expanded[c.check_code] && hasDetail && (
                      <div className="px-3 pb-2 pl-10 text-xs text-muted-foreground space-y-0.5" data-testid={`recon-detail-${c.check_code}`}>
                        {c.employee_details.map((d, i) => (
                          <div key={i}>{d.employee_name ? `${d.employee_name}${d.field ? ` — ${d.field}` : ""}: expected ${money(d.expected)}, got ${money(d.actual)} (diff ${money(d.difference)})` : d.detail}</div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* actions */}
            {status !== "REVERSED" && (
              <div className="flex flex-wrap gap-2" data-testid="workflow-actions">
                {status === "CALCULATED" && isPreparer && <button data-testid="wf-review" disabled={busy} onClick={() => act("review")} className="bg-black text-white px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50">Mark reviewed</button>}
                {status === "REVIEWED" && isAdmin && <button data-testid="wf-approve" disabled={busy || !s?.can_post} onClick={() => act("approve")} className="bg-black text-white px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50" title={!s?.can_post ? "Blocking checks must pass first" : ""}>Approve</button>}
                {status === "APPROVED" && isAdmin && <button data-testid="wf-post" disabled={busy || !s?.can_post} onClick={() => act("post")} className="bg-emerald-700 text-white px-4 py-2 text-sm font-medium hover:opacity-90 disabled:opacity-50">Post payroll</button>}
                {status === "APPROVED" && <span className="text-xs text-muted-foreground self-center">Posting locks totals, GL and CRA liability.</span>}
                {posted && !run.is_reversal && !run.reversed_by_run && isAdmin && <button data-testid="wf-reverse" disabled={busy} onClick={doReverse} className="inline-flex items-center gap-1.5 border border-red-300 text-red-700 px-4 py-2 text-sm hover:bg-red-50 transition-colors"><Undo2 className="h-4 w-4" /> Reverse</button>}
              </div>
            )}
            {status === "REVERSED" && <p className="text-sm text-muted-foreground">This payroll was reversed{run.reversal_reason ? `: ${run.reversal_reason}` : ""}. History is preserved.</p>}
          </>
        )}

        {/* audit trail */}
        {audit.length > 0 && (
          <details className="text-xs" data-testid="workflow-audit">
            <summary className="cursor-pointer text-muted-foreground">Audit trail ({audit.length})</summary>
            <div className="mt-2 space-y-1">
              {audit.map((a) => (
                <div key={a.audit_id} className="flex flex-wrap gap-x-3 text-muted-foreground">
                  <span className="font-medium text-black">{a.action}</span>
                  <span>{a.previous_status || "—"} → {a.new_status || "—"}</span>
                  <span>by {a.actor}</span>
                  <span>{String(a.timestamp).slice(0, 16).replace("T", " ")}</span>
                  {a.note && <span>· {a.note}</span>}
                </div>
              ))}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}
